import {Injectable} from '@angular/core';
import {MasterOperationModel} from 'web-shared';
import {plainToInstance} from 'class-transformer';
import {MasterUseInstructionModel} from 'web-shared';
import {MasterInputInstructionModel, MasterOutputInstructionModel} from 'web-shared';
import {CONSTANTS, MesApiService} from 'common-shared';
import {catchError, map} from 'rxjs/operators';
import {PropertyMappingModel} from 'web-shared';
import {ProcessSelectorModel} from "web-shared";
import {throwError} from "rxjs";
import {ManufacturingItemModel} from "web-shared";
import {ManufacturingItemGroupModel} from "web-shared";
import {TaskSelectorModel} from 'web-shared';
import {ExpressionModel} from "web-shared";
import {IntegratedMasterModel} from "web-shared";
import {
  IPropertyMappingEditorModal
} from "../../../common/property-mapping/property-mapping-editor-modal/property-mapping-editor-modal.component";
import {ResourceQuery} from "../../resources/models/resource.query";

@Injectable({
  providedIn: 'root'
})
export class IntegratedMasterService extends MesApiService {

  constructor() {
    super('IntegratedMaster', 'manufacturing');
  }

  getAll(itemGroupId?: string, itemId?: string, orderId?: string, includeInheritedSpecs?: boolean) {
    return this.getSingle<{ ProcessSelectors: ProcessSelectorModel[], MasterOperations: MasterOperationModel[] }>({
      action: 'MasterOperations'
    }, {
      itemGroupId: itemGroupId || '',
      itemId: itemId || '',
      productionOrderId: orderId || '',
      includeInheritedSpecs: includeInheritedSpecs
    }).pipe(map(value => {
      const r = plainToInstance(IntegratedMasterModel, value.data);
      if (r.ProcessSelectors.length == 0) {
        r.ProcessSelectors = [...r.ProcessSelectors, ProcessSelectorModel.createValid()];
      }
      return r;
    }), catchError(err => {
      if (err.error.data) {
        return throwError(() => plainToInstance(IntegratedMasterModel, err.error.data));
      }
      return throwError(err);
    }));
  }

  getNewMasterOperation(processSelectorId: string = undefined, processNumber: number = 10) {
    return new MasterOperationModel({
      ProcessSelectorId: processSelectorId,
      MasterInputInstructions: [],
      MasterOutputInstructions: [],
      MasterUseInstructions: [],
      ProcessNumber: processNumber
    });
  }

  getNewMasterUseInstruction(mo: MasterOperationModel): MasterUseInstructionModel {
    const mui = Object.assign(new MasterUseInstructionModel(), {
      Code: 'M',
      IsMainResource: true,
      RequiredResourceQuantity: 1,
      ProductionDuration: {Formula: '0'},
      TaskSelectorId: null
    });
    if (mo.MasterUseInstructions.length >= 1) {
      mui.Code = 'S' + (mo.MasterUseInstructions.length > 1 ? mo.MasterUseInstructions.length : '');
      mui.IsMainResource = false;
    }
    return mui;
  }

  getNewMasterInputInstruction(count: number) {
    const number = count + 1;
    let code = 'In';
    if (count > 0) {
      if (number < 10) {
        code += '0';
      }
      code += number;
    }

    return Object.assign(new MasterInputInstructionModel(), {
      Code: code,
      ProductionRatio: {Formula: '1'},
      TimeConstraintMethodClv: CONSTANTS.TimeConstraintMethod.ES
    });
  }

  getNewMasterOutputInstruction(count: number) {
    const number = count + 1;
    let code = 'Out';
    if (count > 0) {
      if (number < 10) {
        code += '0';
      }
      code += number;
    }

    return Object.assign(new MasterOutputInstructionModel(), {Code: code, ProductionRatio: {Formula: '1'}});
  }

  addMasterUseInstructionAfter(mo: MasterOperationModel, mui?: MasterUseInstructionModel, taskSelectorId?: string) {
    let index = -1;
    if (mui) {
      index = mo.MasterUseInstructions.indexOf(mui);
    }

    const newMasterUseInstruction = this.getNewMasterUseInstruction(mo);
    newMasterUseInstruction.TaskSelectorId = taskSelectorId ?? newMasterUseInstruction.TaskSelectorId;
    mo.MasterUseInstructions.splice(index + 1, 0, newMasterUseInstruction);
    mo.MasterUseInstructions = [...mo.MasterUseInstructions];
    return newMasterUseInstruction;
  }

  addMasterInputInstructionAfter(mo: MasterOperationModel, mii: MasterInputInstructionModel, taskSelectorId?: string) {
    let index = -1;
    if (mii) {
      index = mo.MasterInputInstructions.indexOf(mii);
    }

    const newMasterInputInstruction = this.getNewMasterInputInstruction(mo.MasterInputInstructions.length);
    newMasterInputInstruction.TaskSelectorId = taskSelectorId ?? newMasterInputInstruction.TaskSelectorId;
    mo.MasterInputInstructions.splice(index + 1, 0, newMasterInputInstruction);
    mo.MasterInputInstructions = [...mo.MasterInputInstructions];
    return newMasterInputInstruction;
  }

  addMasterOutputInstructionAfter(mo: MasterOperationModel, moi: MasterOutputInstructionModel, taskSelectorId?: string) {
    let index = -1;
    if (moi) {
      index = mo.MasterOutputInstructions.indexOf(moi);
    }

    const newMasterOutputInstruction = this.getNewMasterOutputInstruction(mo.MasterOutputInstructions.length);
    newMasterOutputInstruction.TaskSelectorId = taskSelectorId ?? newMasterOutputInstruction.TaskSelectorId;
    mo.MasterOutputInstructions.splice(index + 1, 0, newMasterOutputInstruction);
    mo.MasterOutputInstructions = [...mo.MasterOutputInstructions];
  }

  removeMasterUseInstruction(mo: MasterOperationModel, mui: MasterUseInstructionModel) {
    mo.MasterUseInstructions = [...mo.MasterUseInstructions.filter(value => value !== mui)];
  }

  removeMasterUseInstructions(mo: MasterOperationModel, muis: MasterUseInstructionModel[]) {
    mo.MasterUseInstructions = [...mo.MasterUseInstructions.filter(value => muis.every(mui => mui !== value))];
  }

  removeMasterInputInstruction(mo: MasterOperationModel, mii: MasterInputInstructionModel) {
    mo.MasterInputInstructions = [...mo.MasterInputInstructions.filter(value => value !== mii)];
  }

  removeMasterInputInstructions(mo: MasterOperationModel, miis: MasterInputInstructionModel[]) {
    mo.MasterInputInstructions = [...mo.MasterInputInstructions.filter(value => miis.every(mii => mii !== value))];
  }

  removeMasterOutputInstruction(mo: MasterOperationModel, moi: MasterOutputInstructionModel) {
    mo.MasterOutputInstructions = [...mo.MasterOutputInstructions.filter(value => value !== moi)];
  }

  removeMasterOutputInstructions(mo: MasterOperationModel, mois: MasterOutputInstructionModel[]) {
    mo.MasterOutputInstructions = [...mo.MasterOutputInstructions.filter(value => mois.every(moi => moi !== value))];
  }

  ensureAnyLine(operationModels: MasterOperationModel[], processSelectors: ProcessSelectorModel[]) {
    if (operationModels?.length === 0) {
      let masterOperationModel = this.getNewMasterOperation();
      masterOperationModel.MasterUseInstructions.push(this.getNewMasterUseInstruction(masterOperationModel));
      masterOperationModel.ProcessSelectorId = processSelectors.length === 1 ? processSelectors[0].Id : null;
      operationModels.push(masterOperationModel);
    }
  }

  editOperationItemProperties(obj: {
    mo: MasterOperationModel,
    mii?: MasterInputInstructionModel,
    moi?: MasterOutputInstructionModel,
    mui?: MasterUseInstructionModel,
    ensureWeightProperty?: boolean,
    readonly?: boolean
  }) {
    const initialState: IPropertyMappingEditorModal = {
      title: '',
      context: undefined,
      sourceType: '',
      isChanged: false,
      readonly: obj.readonly
    };

    if (obj.mo) {
      initialState.title = obj.mo.ProcessNumber.toString();
      initialState.context = obj.mo;
      initialState.sourceType = 'MasterOperation';
    }
    if (obj.mii) {
      initialState.title = (obj.mo.ProcessNumber + (obj.mii.Code ? ': ' + obj.mii.Code : ''));
      initialState.context = obj.mii;
      initialState.sourceType = 'MasterInputInstruction';
    }
    if (obj.moi) {
      initialState.title = (obj.mo.ProcessNumber + (obj.moi.Code ? ': ' + obj.moi.Code : ''));
      initialState.context = obj.moi;
      initialState.sourceType = 'MasterOutputInstruction';
    }
    if (obj.mui) {
      initialState.title = (obj.mo.ProcessNumber + (obj.mui.Code ? ': ' + obj.mui.Code : ''));
      initialState.context = obj.mui;
      initialState.sourceType = 'MasterUseInstruction';
    }

    if (obj.ensureWeightProperty) {
      obj.mii.OperationItemProperties = obj.mii.OperationItemProperties || [];
      const pm = obj.mii.OperationItemProperties.find(oip => {
        return oip.PropertyId === '99d08541-0970-4e2d-a98c-06eccba1c838';
      });
      if (!pm) {
        obj.mii.OperationItemProperties.push(new PropertyMappingModel({
          PropertyId: '99d08541-0970-4e2d-a98c-06eccba1c838',
          MandatoryClv: 'd27e7d24-7767-43a8-8d8a-dcf644ca752d',
          OrderOriginClv: '5695b9ef-49c8-4f3a-ad4a-99abf95f9b9d',
          Expression: new ExpressionModel({Formula: obj.mii.ProductionRatio.Formula}),
          SourceCode: obj.mii.Code,
          SourceId: obj.mii.Id,
          SourceType: 'MasterInputInstruction'
        }));
      }
    }

    return initialState;
  }

  // loadIme(container: ManufacturingItemGroupModel | ManufacturingItemModel | ConfigurationModel, value: { MasterOperations: MasterOperationModel[]; ProcessSelectors: ProcessSelectorModel[] }): void {
  loadIme(container: ManufacturingItemGroupModel | ManufacturingItemModel | any, value: {
    MasterOperations: MasterOperationModel[];
    ProcessSelectors: ProcessSelectorModel[]
  }): void {
    container.MasterOperations = value.MasterOperations;
    container.ProcessSelectors = value.ProcessSelectors;
    this.ensureRowGroupKeys(container);
    container.SaveRouting = true;
  };

  ensureRowGroupKeys(container: ManufacturingItemGroupModel | ManufacturingItemModel | {
    MasterOperations: MasterOperationModel[],
    ProcessSelectors: ProcessSelectorModel[]
  }) {
    container.MasterOperations.forEach(mo => {
      mo.RowGroupKey = this.getRowGroupKey(mo.ProcessSelectorId, container.ProcessSelectors);
    });
  }

  getRowGroupKey(psId: string, processSelectors: ProcessSelectorModel[]) {
    const ps = processSelectors.find(ps => psId === ps.Id);
    return ps?.Id ? `${ps.Sequence}-${ps.Code}-${ps.Id}` : '-';
  }

  public removeTaskSelectorFromOperation(masterOperation: MasterOperationModel, ts: TaskSelectorModel, action: 'deleteWithInstructions' | 'delete') {
    if (action === 'delete') {
      for (let masterInputInstruction of masterOperation.MasterInputInstructions) {
        if (masterInputInstruction.TaskSelectorId === ts.Id) {
          masterInputInstruction.TaskSelectorId = undefined;
        }
      }
      for (let masterUseInstruction of masterOperation.MasterUseInstructions) {
        if (masterUseInstruction.TaskSelectorId === ts.Id) {
          masterUseInstruction.TaskSelectorId = undefined;
        }
      }
      for (let masterOutputInstruction of masterOperation.MasterOutputInstructions) {
        if (masterOutputInstruction.TaskSelectorId === ts.Id) {
          masterOutputInstruction.TaskSelectorId = undefined;
        }
      }
    } else if (action === 'deleteWithInstructions') {
      masterOperation.MasterInputInstructions = [...masterOperation.MasterInputInstructions.filter(mii => mii.TaskSelectorId !== ts.Id)];
      masterOperation.MasterUseInstructions = [...masterOperation.MasterUseInstructions.filter(mui => mui.TaskSelectorId !== ts.Id)];
      masterOperation.MasterOutputInstructions = [...masterOperation.MasterOutputInstructions.filter(moi => moi.TaskSelectorId !== ts.Id)];
    }
    masterOperation.TaskSelectors = [...masterOperation.TaskSelectors.filter(value1 => value1.Id !== ts.Id)];
  }

  updateUserSpecifiedResources(resource: ResourceQuery, masterUseInstruction: MasterUseInstructionModel) {
    if (masterUseInstruction.UserSpecifiedResources.includes(resource.Id)) {
      masterUseInstruction.UserSpecifiedResources = masterUseInstruction.UserSpecifiedResources.filter(id => id != resource.Id);
    } else {
      masterUseInstruction.UserSpecifiedResources.push(resource.Id);
    }
    masterUseInstruction.UserSpecifiedResources = [...masterUseInstruction.UserSpecifiedResources];
  }
}
